import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { documentService } from '../services/document.service';
import { organizationService } from '../services/organization.service'; 
import { ConfirmModal } from '../components/ConfirmModal';
import { OrganizationSettingsModal } from '../components/OrganizationSettingsModal';
import { ManageSubscriptionModal } from '../components/ManageSubscriptionModal';
import { UpgradeModal } from '../components/UpgradeModal';
import { PlanSelectionModal } from '../components/PlanSelectionModal';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { usePWAInstall } from '../hooks/usePWAInstall';

interface DocMetadata {
  id: string;
  title: string;
  updatedAt: string;
  sizeBytes: number;
}

// Define the navigation context type
type ViewContext = { type: 'personal' | 'workspace'; id?: string };

export default function Dashboard() {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<DocMetadata[]>([]);
  const [docToDelete, setDocToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Navigation & Org State
  const [activeView, setActiveView] = useState<ViewContext>({ type: 'personal' });
  const [orgData, setOrgData] = useState<any>(null);

  // Mobile Drawer State
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Modal States
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isPlanSelectionOpen, setIsPlanSelectionOpen] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState<'PRO' | 'TEAM' | null>(null);
  
  const { token, user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const { isInstallable, promptInstall } = usePWAInstall();

  /**
   * Loads both the documents for the current view and the organization structure
   */
  const loadData = async () => {
    if (!token) return;
    try {
      // 1. Load Documents based on current view context
      let docParams: { type?: 'personal' | 'team'; workspaceId?: string } = {};
      
      if (activeView.type === 'personal') {
        docParams = { type: 'personal' };
      } else {
        docParams = { workspaceId: activeView.id };
      }
      
      const docData = await documentService.getDocuments(token, docParams);
      setDocuments(docData.documents);

      // 2. Load Organization/Workspaces if the user is in a Team
      if (user?.organizationId) {
        const data = await organizationService.getOrganization(token, user.organizationId);
        setOrgData(data.organization);
      }
    } catch (err) {
      console.error("Dashboard Load Error:", err);
    }
  };

  useEffect(() => {
    loadData();
  }, [token, activeView, user?.organizationId]);

  const handleCreateDoc = async () => {
    try {
      setError(null);
      // Pass workspaceId if we are currently inside a workspace view
      const workspaceId = activeView.type === 'workspace' ? activeView.id : undefined;
      const { document } = await documentService.createDocument(token!, workspaceId);
      navigate(`/d/${document.id}`);
    } catch (err) {
      setError("Failed to create spreadsheet. You may have exceeded your storage quota.");
    }
  };

  const getBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
    };
    reader.onerror = error => reject(error);
  });

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      const base64 = await getBase64(file);
      
      const workspaceId = activeView.type === 'workspace' ? activeView.id : undefined;
      const title = file.name.replace(/\.[^/.]+$/, "");
      
      const { document } = await documentService.createDocument(token!, workspaceId, title);
      
      navigate(`/d/${document.id}`, { state: { importBase64: base64, fileName: file.name } });
    } catch (err) {
      setError("Failed to import spreadsheet.");
    }
    
    // Clear the input
    e.target.value = '';
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    try {
      setError(null);
      await documentService.deleteDocument(token!, docToDelete);
      setTimeout(() => loadData(), 500);
    } catch (err) {
      setError("Error deleting the document.");
    } finally {
      setDocToDelete(null);
    }
  };

  const handleSelectPlan = (plan: 'PRO' | 'TEAM') => {
    setIsPlanSelectionOpen(false);
    setUpgradePlan(plan);
  };

  const handleUpgradeSuccess = () => {
    setSuccessMsg(`Upgrade successful! Enjoy your new ${upgradePlan} features.`);
    if (upgradePlan === 'PRO') {
      updateUser({ tier: 'PRO' });
    } else {
      // Refresh to establish the new Organization relationships
      setTimeout(() => window.location.reload(), 1500);
    }
  };

  const maxStorage = (user?.tier === 'PRO' || user?.tier === 'TEAM') ? 500 * 1024 * 1024 : 50 * 1024 * 1024;
  const storageUsed = user?.storageUsed || 0;
  const storagePercent = Math.min(100, (storageUsed / maxStorage) * 100);
  
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 MB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Helper to find the current Workspace name
  const currentWorkspaceName = activeView.type === 'workspace' 
    ? orgData?.workspaces.find((w: any) => w.id === activeView.id)?.name 
    : t('nav.myDocuments');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      
      {/* Mobile Header & Hamburger */}
      <div className="md:hidden flex items-center justify-between bg-white p-4 border-b border-gray-200 shrink-0 z-20">
        <div className="flex items-center">
            <img src="/logo-57w.png" className="mr-2 inline h-6" alt="Logo" />
            <h2 className="text-xl font-bold text-red-700 tracking-tight">{t('app.title')}</h2>
        </div>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
          className="text-gray-600 focus:outline-none p-2 rounded-md hover:bg-gray-100"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* SIDEBAR NAVIGATION */}
      <div className={`fixed inset-0 z-40 flex transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:w-64 md:shrink-0`}>
        {/* Dark overlay for mobile */}
        {isMobileMenuOpen && (
            <div className="fixed inset-0 bg-black/50 md:hidden" onClick={() => setIsMobileMenuOpen(false)}></div>
        )}
        
        <aside className="w-64 bg-white border-r border-gray-200 p-6 flex flex-col shrink-0 h-full relative z-50">
          <div className="mb-8">
            <div className="hidden md:flex items-center justify-between mb-1">
              <h2 className="text-2xl font-bold text-red-700 tracking-tight flex items-center"><img src="/logo-57w.png" className="mr-2 inline h-6" alt="Logo" />{t('app.title')}</h2>
            </div>
            {/* On mobile, we only need to show the Language switcher at the top since title is in the mobile header */}
            <div className="md:hidden flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">{user?.tier || 'FREE'} PLAN</p>
                <LanguageSwitcher />
            </div>
            
            <p className="hidden md:block text-sm text-gray-500 font-medium mt-1 uppercase tracking-wider">
              {user?.tier || 'FREE'} PLAN
            </p>
          </div>

          <nav className="flex-1 space-y-6">
            {/* PERSONAL SECTION */}
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 px-2">{t('nav.personal')}</h3>
              <button 
                onClick={() => { setActiveView({ type: 'personal' }); setIsMobileMenuOpen(false); }}
                className={`w-full flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeView.type === 'personal' ? 'bg-red-50 text-red-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                {t('nav.myDocuments')}
              </button>
            </div>

            {/* TEAM SECTION (Dynamic based on Org Membership) */}
            {user?.organizationId && orgData && (
              <div>
                <div className="flex justify-between items-center mb-3 px-2">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest truncate max-w-[120px]">
                    {orgData.name}
                  </h3>
                  <button 
                    onClick={() => { setIsOrgModalOpen(true); setIsMobileMenuOpen(false); }}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    title="Team Settings"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
                  </button>
                </div>
                <div className="space-y-1">
                  {orgData.workspaces.map((ws: any) => (
                    <button 
                      key={ws.id}
                      onClick={() => { setActiveView({ type: 'workspace', id: ws.id }); setIsMobileMenuOpen(false); }}
                      className={`w-full flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeView.id === ws.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="mr-3 opacity-50">#</span>
                      {ws.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* UPGRADE BUTTON */}
            {user?.tier !== 'TEAM' && (
               <button 
                 onClick={() => { setIsPlanSelectionOpen(true); setIsMobileMenuOpen(false); }} 
                 className="w-full flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 font-bold rounded-lg transition-colors text-left"
               >
                 <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                 {t('nav.upgradePlan')}
               </button>
            )}

            {user?.tier !== 'FREE' && (
                <button 
                  onClick={() => { setIsSubscriptionModalOpen(true); setIsMobileMenuOpen(false); }} 
                  className="w-full flex items-center px-4 py-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 font-medium rounded-lg transition-colors text-left mt-4 border border-gray-200"
                >
                  {t('nav.manageBilling')}
                </button>
            )}

            {/* PWA INSTALL BUTTON */}
            {isInstallable && (
                <button 
                  onClick={async () => {
                      const res = await promptInstall();
                      if (res === 'ios_instructions') alert(t('install.iosInstructions'));
                  }} 
                  className="w-full md:hidden flex items-center px-4 py-2 text-green-700 bg-green-50 hover:bg-green-100 font-bold rounded-lg transition-colors text-left mt-4 border border-green-200"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                  {t('nav.installApp')}
                </button>
            )}
          </nav>

          {/* STORAGE QUOTA BAR */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex justify-between text-xs font-bold text-gray-600 mb-2">
              <span>Storage Used</span>
              <span className={storagePercent > 90 ? 'text-red-600' : ''}>
                {storagePercent.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2 overflow-hidden">
              <div 
                className={`h-2.5 rounded-full ${storagePercent > 90 ? 'bg-red-600' : 'bg-blue-600'}`} 
                style={{ width: `${storagePercent}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500">
              {formatBytes(storageUsed)} of {formatBytes(maxStorage)}
            </p>
          </div>

<div className="mt-6 flex flex-col space-y-2">
          <LanguageSwitcher />
</div>
  
          <div className="mt-6 flex flex-col space-y-2">
            <button onClick={() => { navigate('/profile'); setIsMobileMenuOpen(false); }} className="text-sm text-gray-600 hover:text-gray-900 font-medium transition w-full text-left">
              {t('nav.profileSettings')}
            </button>
            <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="text-sm text-gray-400 hover:text-red-600 font-medium transition w-full text-left">
              {t('nav.logout', { name: user?.name.split(' ')[0] })}
            </button>
          </div>
        </aside>
      </div>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 p-4 md:p-8 overflow-y-auto w-full">
        <div className="max-w-6xl mx-auto">
          
          <header className="flex flex-col sm:flex-row sm:justify-between sm:items-end mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{currentWorkspaceName}</h1>
              <p className="text-gray-500 mt-1">
                {activeView.type === 'personal' ? t('dashboard.privateSpreadsheets') : t('dashboard.sharedWorkspace')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                onClick={() => fileInputRef.current?.click()} 
                className="w-full sm:w-auto bg-white text-gray-700 border border-gray-300 px-4 py-2.5 rounded-lg shadow-sm hover:bg-gray-50 transition font-bold whitespace-nowrap"
              >
                {t('dashboard.importFile')}
              </button>
              <button 
                onClick={handleCreateDoc} 
                className="w-full sm:w-auto bg-red-600 text-white px-6 py-2.5 rounded-lg shadow-lg hover:bg-red-700 transition font-bold whitespace-nowrap"
              >
                {activeView.type === 'personal' ? t('dashboard.createPrivateDoc') : t('dashboard.createTeamDoc')}
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept=".xlsx,.ods,.csv,.tsv" 
                onChange={handleImportFile} 
              />
            </div>
          </header>

          {/* STATUS NOTIFICATIONS */}
          {successMsg && (
              <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-lg border border-green-200 font-medium flex justify-between">
                  {successMsg}
                  <button onClick={() => setSuccessMsg(null)} className="text-green-600 font-bold">✕</button>
              </div>
          )}

          {error && (
              <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg border border-red-200 font-medium">
                  {error}
              </div>
          )}

          {/* DOCUMENT GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {documents.map((doc) => (
              <Link 
                key={doc.id} 
                to={`/d/${doc.id}`}
                className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:border-red-400 hover:shadow-md transition-all relative group flex flex-col h-40"
              >
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-800 mb-1 truncate pr-6">{doc.title}</h3>
                  <p className="text-xs text-gray-400 font-medium">{t('dashboard.size', { size: formatBytes(doc.sizeBytes) })}</p>
                </div>
                
                <div className="mt-auto border-t border-gray-100 pt-3 flex justify-between items-center">
                   <p className="text-xs text-gray-400">{t('dashboard.edited', { date: new Date(doc.updatedAt).toLocaleDateString() })}</p>
                </div>
                
                <button 
                  onClick={(e) => { e.preventDefault(); setDocToDelete(doc.id); }}
                  className="absolute top-3 right-3 text-gray-300 hover:text-red-500 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-full hover:bg-red-50"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              </Link>
            ))}
          </div>

          {/* EMPTY STATE */}
          {documents.length === 0 && (
            <div className="text-center py-16 md:py-24 bg-white rounded-xl border-2 border-dashed border-gray-200 mt-6 shadow-sm px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4">
                 <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
              </div>
              <p className="text-gray-500 text-lg font-medium">{t('dashboard.noSpreadsheets')}</p>
              <p className="text-gray-400 text-sm mt-1">{t('dashboard.startNew')}</p>
            </div>
          )}

          {/* MODALS */}
          <ConfirmModal
            isOpen={!!docToDelete}
            title="Delete Spreadsheet"
            message="Are you sure? This will permanently delete your data."
            confirmText="Delete"
            isDestructive={true}
            onConfirm={confirmDelete}
            onCancel={() => setDocToDelete(null)}
          />

          <OrganizationSettingsModal isOpen={isOrgModalOpen} onClose={() => setIsOrgModalOpen(false)} />
          <ManageSubscriptionModal isOpen={isSubscriptionModalOpen} onClose={() => setIsSubscriptionModalOpen(false)} />

          <PlanSelectionModal 
            isOpen={isPlanSelectionOpen} 
            onClose={() => setIsPlanSelectionOpen(false)} 
            onSelectPlan={handleSelectPlan} 
          />

          <UpgradeModal 
            isOpen={!!upgradePlan} 
            plan={upgradePlan} 
            onClose={() => setUpgradePlan(null)} 
            onSuccess={handleUpgradeSuccess} 
          />
        </div>
      </main>
    </div>
  );
}